/**
 * SettingsHubLayout — Iter 189g Phase 1
 *
 * Squarespace 7.1 style two-column shell for the Settings hub.
 *   • Left rail  — persistent categorised nav (SETTINGS_GROUPS)
 *   • Right pane — <Outlet /> renders the active sub-page
 *
 * The rail auto-highlights the current route and supports keyboard nav
 * (up/down through items, Enter to activate). Group headers are non-
 * interactive; "stub" and "soon" items get a subtle badge but remain
 * clickable so users see the placeholder screen.
 *
 * Uses the existing `DashboardBackdrop` to stay visually consistent with
 * the rest of the dashboard.
 */
import React from 'react';
import { Outlet } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { DashboardBackdrop } from '@/components/dashboard/dashboard-ui';
import { SETTINGS_GROUPS } from './settingsGroups';

function StatusPill({ status }) {
  if (status === 'stub') return <span className="ml-auto text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/25">Soon</span>;
  if (status === 'soon') return <span className="ml-auto text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-white/10 text-white/50">Planned</span>;
  return null;
}

export default function SettingsHubLayout() {
  return (
    <div className="relative" data-testid="settings-hub-layout">
      <DashboardBackdrop />
      <div className="relative z-10 pb-12">
        {/* Iter 191c — Removed the top "Site Settings" heading + description +
            middle left-rail nav. The sidebar's Settings sub-menu now surfaces
            every settings page (SETTINGS_GROUPS flattened) and each child
            settings page owns its own top rail + breadcrumb, so this outer
            header was duplicating chrome. SettingsHubLayout is kept as the
            routing shell + backdrop wrapper so all `/settings/*` child routes
            still render inside DashboardBackdrop. */}
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 *  SettingsPlaceholder — used for stub / soon routes so any left-rail
 *  click always lands on something rendered inside the shell.
 * ─────────────────────────────────────────────────────────── */
export function SettingsPlaceholder({ itemId }) {
  const flat = SETTINGS_GROUPS.flatMap((g) => g.items);
  const item = flat.find((x) => x.id === itemId) || { label: 'Coming Soon', status: 'soon' };
  const Icon = item.icon;
  return (
    <div className="max-w-2xl" data-testid={`settings-placeholder-${itemId}`}>
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#A78BFA]">
        {item._groupLabel || 'Settings'}
      </p>
      <h2 className="mt-2 text-2xl font-bold text-white font-syne">{item.label}</h2>

      <div
        className="mt-8 p-10 rounded-2xl text-center"
        style={{
          background: 'linear-gradient(180deg, rgba(20,15,45,0.5) 0%, rgba(14,14,35,0.85) 100%)',
          border: '1px solid rgba(167,139,250,0.22)',
          backdropFilter: 'blur(16px)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div
          className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.25), rgba(236,72,153,0.15))', border: '1px solid rgba(167,139,250,0.35)' }}
        >
          {Icon ? <Icon size={26} className="text-[#A78BFA]" /> : <ChevronRight size={26} className="text-[#A78BFA]" />}
        </div>
        <h3 className="text-xl font-bold text-white font-syne">
          {item.status === 'stub' ? 'Wave 2 · Coming soon' : 'Planned for a future release'}
        </h3>
        <p className="text-sm text-white/60 mt-3 max-w-md mx-auto leading-relaxed">
          The {item.label.toLowerCase()} controls are queued up for a future iteration.
          The shell and left-rail nav are live so you can navigate the full settings surface,
          but the actual controls for this screen aren&rsquo;t wired yet.
        </p>
      </div>
    </div>
  );
}
